import * as React from 'react';
import { DataType, DetectedPdfField, FieldConfig } from '@/interfaces';

export type FieldConfigsMap = Record<string, FieldConfig>;

export interface UseFieldConfigsResult {
	fieldConfigs: FieldConfigsMap;
	onDataTypeChange: (fieldId: string, dataType: DataType) => void;
	onRenameChange: (fieldId: string, newLabel: string) => void;
}

export const useFieldConfigs = (
	detectedFields: DetectedPdfField[],
	initialConfigs: FieldConfigsMap = {}
): UseFieldConfigsResult => {
	const [fieldConfigs, setFieldConfigs] =
		React.useState<FieldConfigsMap>(initialConfigs);

	React.useEffect(() => {
		if (detectedFields.length === 0) return;

		setFieldConfigs((current) => {
			let hasNewEntries = false;
			const next = { ...current };

			detectedFields.forEach((field) => {
				if (next[field.id]) return;
				hasNewEntries = true;
				next[field.id] = {
					fieldName: field.fieldName,
					dataType: 'DO_NOT_VALIDATE',
					renamedLabel: field.fieldName,
				};
			});

			return hasNewEntries ? next : current;
		});
	}, [detectedFields]);

	const onDataTypeChange = React.useCallback(
		(fieldId: string, dataType: DataType) => {
			setFieldConfigs((current) => {
				const existing = current[fieldId];
				if (!existing) return current;
				return { ...current, [fieldId]: { ...existing, dataType } };
			});
		},
		[]
	);

	const onRenameChange = React.useCallback(
		(fieldId: string, newLabel: string) => {
			setFieldConfigs((current) => {
				const existing = current[fieldId];
				if (!existing) return current;
				return {
					...current,
					[fieldId]: { ...existing, renamedLabel: newLabel },
				};
			});
		},
		[]
	);

	return { fieldConfigs, onDataTypeChange, onRenameChange };
};

export default useFieldConfigs;
